import { useState, useEffect, useCallback } from 'react';
import { getReceipts, uploadReceipt, updateReceipt, deleteReceipt, getReceipt, getReceiptPage, getProperties, getUnits, getKostenstellen } from '../services/api';
import { Receipt, Upload, Trash, Eye, X, CloudArrowUp, FileText, Check, Warning, Image, Robot, CaretLeft, CaretRight, Plus, Stack } from '@phosphor-icons/react';
import AICheckModal from './AICheckModal';

export default function Receipts() {
  const [receipts, setReceipts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [kostenstellen, setKostenstellen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [aiReceipt, setAiReceipt] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);  // files queued for upload
  const [uploadResult, setUploadResult] = useState(null);  // compression info after success

  const loadData = async () => {
    try {
      const [receiptsRes, propertiesRes, unitsRes, ksRes] = await Promise.all([
        getReceipts(),
        getProperties(),
        getUnits(),
        getKostenstellen()
      ]);
      setReceipts(receiptsRes.data);
      setProperties(propertiesRes.data);
      setUnits(unitsRes.data);
      setKostenstellen(ksRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const queueFiles = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const newFiles = Array.from(fileList).map(file => ({
      file,
      name: file.name,
      size: file.size,
      previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
      isPdf: file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf'),
    }));
    setPendingFiles(prev => [...prev, ...newFiles]);
  };

  const removePending = (index) => {
    setPendingFiles(prev => {
      const next = [...prev];
      if (next[index]?.previewUrl) URL.revokeObjectURL(next[index].previewUrl);
      next.splice(index, 1);
      return next;
    });
  };

  const clearPending = () => {
    pendingFiles.forEach(f => f.previewUrl && URL.revokeObjectURL(f.previewUrl));
    setPendingFiles([]);
  };

  const submitUpload = async () => {
    if (pendingFiles.length === 0) return;
    setUploading(true);
    setUploadProgress(0);
    setUploadResult(null);
    try {
      const files = pendingFiles.map(p => p.file);
      const res = await uploadReceipt(files, (evt) => {
        if (evt.total) setUploadProgress((evt.loaded / evt.total) * 100);
      });
      setUploadResult(res.data);
      clearPending();
      loadData();
    } catch (err) {
      console.error('Upload failed:', err);
      alert('Upload fehlgeschlagen: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      queueFiles(e.dataTransfer.files);
    }
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Möchten Sie diesen Beleg wirklich löschen?')) return;
    try {
      await deleteReceipt(id);
      loadData();
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '—';
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const getPropertyName = (propertyId) => {
    const prop = properties.find(p => p.id === propertyId);
    return prop?.name || '—';
  };

  const getKostenstelleName = (ksId) => {
    const ks = kostenstellen.find(k => k.id === ksId);
    return ks?.name || '—';
  };

  const getStatusBadge = (status) => {
    const styles = {
      'neu': 'bg-blue-100 text-blue-800 border-blue-200',
      'zugeordnet': 'bg-emerald-100 text-emerald-800 border-emerald-200',
      'gebucht': 'bg-slate-100 text-slate-800 border-slate-200',
    };
    const labels = {
      'neu': 'Neu',
      'zugeordnet': 'Zugeordnet',
      'gebucht': 'Gebucht',
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border ${styles[status] || styles.neu}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="receipts-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-slate-900">Belege</h1>
        <p className="text-slate-500 mt-1 text-sm sm:text-base">Belege scannen und Kostenstellen zuordnen (OCR)</p>
      </div>

      {/* Upload Zone */}
      <div
        className={`border-2 border-dashed p-8 sm:p-12 text-center transition-all duration-300 ${
          dragActive 
            ? 'border-slate-600 bg-slate-50' 
            : 'border-gray-300 bg-gray-50 hover:border-slate-500 hover:bg-slate-50/50'
        } ${uploading ? 'pointer-events-none opacity-60' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        data-testid="upload-receipt-dropzone"
      >
        {uploading ? (
          <div className="space-y-4">
            <div className="w-16 h-16 mx-auto relative">
              <FileText weight="light" className="w-16 h-16 text-slate-400" />
              <div className="absolute inset-0 border-4 border-slate-600 rounded-full border-t-transparent animate-spin"></div>
            </div>
            <div>
              <p className="font-heading text-lg font-bold text-slate-900">OCR läuft...</p>
              <p className="text-sm text-slate-500 mt-1">Text wird aus dem Beleg extrahiert</p>
            </div>
            <div className="w-64 mx-auto bg-slate-200 h-2">
              <div 
                className="bg-slate-600 h-2 transition-all duration-300" 
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          </div>
        ) : (
          <>
            <CloudArrowUp weight="light" className="w-12 h-12 sm:w-16 sm:h-16 text-slate-400 mx-auto mb-4" />
            <p className="font-heading text-base sm:text-lg font-bold text-slate-900 mb-2">
              Belege hier ablegen
            </p>
            <p className="text-xs sm:text-sm text-slate-500 mb-4">
              oder klicken zum Auswählen (JPG, PNG, PDF)
            </p>
            <label className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 sm:px-6 py-2.5 font-medium hover:bg-slate-800 cursor-pointer">
              <Upload weight="bold" className="w-4 h-4" />
              Belege hochladen
              <input
                type="file"
                multiple
                accept="image/*,.pdf"
                className="hidden"
                onChange={(e) => { queueFiles(e.target.files); e.target.value = ''; }}
                data-testid="file-input"
              />
            </label>
            <p className="text-xs text-slate-400 mt-3">
              <Stack weight="bold" className="inline w-3 h-3 mr-1" />
              Mehrere Seiten möglich · Bilder werden automatisch komprimiert (max. 1600px, JPEG ~78%)
            </p>
          </>
        )}
      </div>

      {/* Staging Area – pending files */}
      {pendingFiles.length > 0 && !uploading && (
        <div className="bg-white border border-slate-200 p-4" data-testid="pending-files-area">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-base font-bold text-slate-900 flex items-center gap-2">
              <Stack weight="bold" className="w-5 h-5 text-slate-700" />
              {pendingFiles.length} {pendingFiles.length === 1 ? 'Seite' : 'Seiten'} bereit zum Hochladen
            </h3>
            <button
              type="button"
              onClick={clearPending}
              className="text-xs text-slate-500 hover:text-red-600"
              data-testid="clear-pending-button"
            >Alle entfernen</button>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 mb-4">
            {pendingFiles.map((p, i) => (
              <div key={i} className="relative group border border-slate-200 bg-slate-50 aspect-[3/4] overflow-hidden" data-testid={`pending-file-${i}`}>
                {p.previewUrl ? (
                  <img src={p.previewUrl} alt={p.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-slate-400">
                    <FileText weight="light" className="w-8 h-8" />
                    <span className="text-[10px] mt-1">PDF</span>
                  </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1 py-0.5 truncate">
                  {i + 1}. {p.name}
                </div>
                <button
                  type="button"
                  onClick={() => removePending(i)}
                  className="absolute top-1 right-1 bg-red-600 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Entfernen"
                  data-testid={`remove-pending-${i}`}
                >
                  <X weight="bold" className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">
            <p className="text-xs text-slate-500">
              <Check weight="bold" className="inline w-3 h-3 text-emerald-600 mr-1" />
              Tipp: Sie können noch weitere Seiten per Drag & Drop hinzufügen.
            </p>
            <button
              type="button"
              onClick={submitUpload}
              disabled={uploading}
              className="inline-flex items-center gap-2 bg-emerald-700 text-white px-5 py-2.5 font-medium hover:bg-emerald-800 disabled:opacity-50"
              data-testid="submit-upload-button"
            >
              <CloudArrowUp weight="bold" className="w-4 h-4" />
              {pendingFiles.length === 1 ? 'Beleg hochladen' : `${pendingFiles.length} Seiten als 1 Beleg hochladen`}
            </button>
          </div>
        </div>
      )}

      {/* Upload Result – compression stats */}
      {uploadResult && (
        <div className="bg-emerald-50 border border-emerald-200 p-4 flex items-start gap-3" data-testid="upload-result-info">
          <Check weight="fill" className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm text-emerald-900">
            <p className="font-medium">Beleg erfolgreich hochgeladen – {uploadResult.page_count} {uploadResult.page_count === 1 ? 'Seite' : 'Seiten'}</p>
            {uploadResult.kompression && (
              <p className="text-xs mt-1 opacity-90">
                Komprimierung: {(uploadResult.kompression.original_bytes / 1024 / 1024).toFixed(2)} MB →
                {' '}{(uploadResult.kompression.gespeichert_bytes / 1024 / 1024).toFixed(2)} MB
                {' '}(<strong>{uploadResult.kompression.ersparnis_prozent}% gespart</strong>)
              </p>
            )}
          </div>
          <button onClick={() => setUploadResult(null)} className="text-emerald-700 hover:text-emerald-900">
            <X weight="bold" className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Receipts List */}
      {receipts.length === 0 ? (
        <div className="bg-white border border-gray-200 p-8 sm:p-12 text-center">
          <Receipt weight="light" className="w-12 h-12 sm:w-16 sm:h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="font-heading text-lg sm:text-xl font-bold text-slate-900 mb-2">Keine Belege</h3>
          <p className="text-slate-500 text-sm sm:text-base">Laden Sie Ihren ersten Beleg hoch.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {receipts.map((receipt) => (
            <div 
              key={receipt.id} 
              className="bg-white border border-gray-200 p-4 flex flex-col sm:flex-row sm:items-center gap-4"
              data-testid={`receipt-row-${receipt.id}`}
            >
              {/* Status & Info */}
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className={`w-10 h-10 flex-shrink-0 flex items-center justify-center ${
                  receipt.status === 'zugeordnet' || receipt.status === 'gebucht' ? 'bg-emerald-100' : 'bg-blue-100'
                }`}>
                  {receipt.status === 'zugeordnet' || receipt.status === 'gebucht' ? (
                    <Check weight="bold" className="w-5 h-5 text-emerald-600" />
                  ) : (
                    <FileText weight="bold" className="w-5 h-5 text-blue-600" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-900 truncate flex items-center gap-2">
                    {receipt.vendor || receipt.filename}
                    {receipt.page_count > 1 && (
                      <span className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wider font-bold bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded border border-slate-200" data-testid={`pages-badge-${receipt.id}`}>
                        <Stack weight="bold" className="w-3 h-3" />
                        {receipt.page_count}
                      </span>
                    )}
                  </p>
                  <p className="text-xs sm:text-sm text-slate-500 truncate">
                    {receipt.date || 'Kein Datum'} 
                    {receipt.kostenstelle_id && ` • ${getKostenstelleName(receipt.kostenstelle_id)}`}
                  </p>
                </div>
              </div>

              {/* Amount & Status */}
              <div className="flex items-center justify-between sm:justify-end gap-4 sm:gap-6">
                <div className="text-left sm:text-right">
                  <p className="font-mono font-medium text-slate-900">
                    {formatCurrency(receipt.amount)}
                  </p>
                  {receipt.umlagefaehig !== null && (
                    <span className={`text-xs ${receipt.umlagefaehig ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {receipt.umlagefaehig ? 'Umlagefähig' : 'Nicht umlagef.'}
                    </span>
                  )}
                </div>
                {getStatusBadge(receipt.status)}
                <div className="flex gap-1">
                  <button
                    onClick={() => setAiReceipt(receipt)}
                    className="p-2 text-slate-400 hover:text-violet-700 hover:bg-violet-50"
                    data-testid={`ai-check-${receipt.id}`}
                    title="Mit KI prüfen (IONOS, DSGVO-konform)"
                  >
                    <Robot weight="bold" className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setSelectedReceipt(receipt)}
                    className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                    data-testid={`view-receipt-${receipt.id}`}
                  >
                    <Eye weight="bold" className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(receipt.id)}
                    className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50"
                    data-testid={`delete-receipt-${receipt.id}`}
                  >
                    <Trash weight="bold" className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Receipt Detail Modal */}
      {selectedReceipt && (
        <ReceiptDetailModal
          receipt={selectedReceipt}
          properties={properties}
          units={units}
          kostenstellen={kostenstellen}
          onClose={() => setSelectedReceipt(null)}
          onSave={() => { loadData(); setSelectedReceipt(null); }}
        />
      )}

      {/* AI Check Modal */}
      {aiReceipt && (
        <AICheckModal
          receipt={aiReceipt}
          kostenstellen={kostenstellen}
          onClose={() => setAiReceipt(null)}
          onAccepted={() => loadData()}
        />
      )}
    </div>
  );
}

function ReceiptDetailModal({ receipt, properties, units, kostenstellen, onClose, onSave }) {
  const [formData, setFormData] = useState({
    vendor: receipt.vendor || '',
    amount: receipt.amount || '',
    date: receipt.date || '',
    property_id: receipt.property_id || '',
    unit_id: receipt.unit_id || '',
    kostenstelle_id: receipt.kostenstelle_id || '',
    umlagefaehig: receipt.umlagefaehig,
    status: receipt.status || 'neu'
  });
  const [loading, setLoading] = useState(false);
  const [showOcrText, setShowOcrText] = useState(false);
  const [imageData, setImageData] = useState(null);
  const [loadingImage, setLoadingImage] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCount, setPageCount] = useState(receipt.page_count || 1);

  // Load page (initial + on navigation)
  useEffect(() => {
    let cancelled = false;
    const loadPage = async () => {
      setLoadingImage(true);
      try {
        if (currentPage === 0 && (!receipt.page_count || receipt.page_count <= 1)) {
          // legacy single-page receipt: full GET keeps backward-compat
          const res = await getReceipt(receipt.id);
          if (cancelled) return;
          if (res.data.file_data) {
            setImageData(`data:${res.data.mime_type || 'image/jpeg'};base64,${res.data.file_data}`);
          } else {
            setImageData(null);
          }
          setPageCount(res.data.page_count || 1);
        } else {
          const res = await getReceiptPage(receipt.id, currentPage);
          if (cancelled) return;
          if (res.data?.data) {
            setImageData(`data:${res.data.mime_type || 'image/jpeg'};base64,${res.data.data}`);
          } else {
            setImageData(null);
          }
          setPageCount(res.data.page_count || 1);
        }
      } catch (err) {
        console.error('Failed to load page:', err);
        if (!cancelled) setImageData(null);
      } finally {
        if (!cancelled) setLoadingImage(false);
      }
    };
    loadPage();
    return () => { cancelled = true; };
  }, [receipt.id, currentPage, receipt.page_count]);

  // Auto-set umlagefaehig based on kostenstelle
  useEffect(() => {
    if (formData.kostenstelle_id) {
      const ks = kostenstellen.find(k => k.id === formData.kostenstelle_id);
      if (ks) {
        setFormData(prev => ({ ...prev, umlagefaehig: ks.umlagefaehig }));
      }
    }
  }, [formData.kostenstelle_id, kostenstellen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await updateReceipt(receipt.id, {
        ...formData,
        amount: formData.amount ? parseFloat(formData.amount) : null,
        property_id: formData.property_id || null,
        unit_id: formData.unit_id || null,
        kostenstelle_id: formData.kostenstelle_id || null,
        status: formData.kostenstelle_id ? 'zugeordnet' : formData.status
      });
      onSave();
    } catch (err) {
      console.error('Failed to update:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredUnits = formData.property_id 
    ? units.filter(u => u.property_id === formData.property_id)
    : [];

  const umlagefaehigeKs = kostenstellen.filter(k => k.umlagefaehig);
  const nichtUmlagefaehigeKs = kostenstellen.filter(k => !k.umlagefaehig);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="receipt-modal">
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <div>
            <h2 className="font-heading text-lg sm:text-xl font-bold text-slate-900">Beleg bearbeiten</h2>
            <p className="text-sm text-slate-500 truncate max-w-xs">{receipt.filename}</p>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600">
            <X weight="bold" className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-6">
          {/* Image Preview */}
          <div className="bg-slate-50 border border-slate-200">
            <div className="px-4 py-2 border-b border-slate-200 flex items-center justify-between gap-2">
              <p className="text-xs tracking-widest uppercase font-bold text-slate-500 flex items-center gap-2">
                <Image weight="bold" className="w-4 h-4" />
                Beleg-Vorschau
                {pageCount > 1 && (
                  <span className="text-slate-700 normal-case tracking-normal" data-testid="page-indicator">
                    · Seite {currentPage + 1} / {pageCount}
                  </span>
                )}
              </p>
              {pageCount > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                    disabled={currentPage === 0 || loadingImage}
                    className="p-1.5 text-slate-600 hover:text-slate-900 disabled:opacity-30 border border-slate-200 rounded"
                    data-testid="page-prev-button"
                    title="Vorherige Seite"
                  >
                    <CaretLeft weight="bold" className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(p => Math.min(pageCount - 1, p + 1))}
                    disabled={currentPage >= pageCount - 1 || loadingImage}
                    className="p-1.5 text-slate-600 hover:text-slate-900 disabled:opacity-30 border border-slate-200 rounded"
                    data-testid="page-next-button"
                    title="Nächste Seite"
                  >
                    <CaretRight weight="bold" className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
            <div className="p-4">
              {loadingImage ? (
                <div className="h-48 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-600"></div>
                </div>
              ) : imageData ? (
                <div className="relative">
                  <img 
                    src={imageData} 
                    alt={receipt.filename}
                    className="max-h-64 w-full object-contain bg-white border border-slate-200"
                    data-testid="receipt-image-preview"
                  />
                  <a
                    href={imageData}
                    download={receipt.filename}
                    className="absolute top-2 right-2 bg-slate-900 text-white px-3 py-1 text-xs font-medium hover:bg-slate-800"
                  >
                    Herunterladen
                  </a>
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center text-slate-400">
                  <FileText weight="light" className="w-12 h-12" />
                </div>
              )}
              {pageCount > 1 && (
                <div className="flex justify-center gap-1.5 mt-3" data-testid="page-dots">
                  {Array.from({ length: pageCount }).map((_, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setCurrentPage(idx)}
                      className={`w-2 h-2 rounded-full transition-colors ${idx === currentPage ? 'bg-slate-900' : 'bg-slate-300 hover:bg-slate-500'}`}
                      title={`Seite ${idx + 1}`}
                      data-testid={`page-dot-${idx}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* OCR Text Preview */}
          {receipt.ocr_text && (
            <div className="bg-slate-50 border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs tracking-widest uppercase font-bold text-slate-500">
                  OCR-Text (erkannt)
                </p>
                <button
                  type="button"
                  onClick={() => setShowOcrText(!showOcrText)}
                  className="text-xs text-slate-600 hover:text-slate-900"
                >
                  {showOcrText ? 'Ausblenden' : 'Anzeigen'}
                </button>
              </div>
              {showOcrText && (
                <pre className="text-xs text-slate-600 whitespace-pre-wrap max-h-48 overflow-y-auto font-mono bg-white p-3 border border-slate-200">
                  {receipt.ocr_text}
                </pre>
              )}
            </div>
          )}

          {/* Basic Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Lieferant/Firma</label>
              <input
                type="text"
                value={formData.vendor}
                onChange={(e) => setFormData(prev => ({ ...prev, vendor: e.target.value }))}
                className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 outline-none"
                placeholder="z.B. Handwerker GmbH"
                data-testid="receipt-vendor-input"
              />
            </div>
            <div>
              <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Datum</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 outline-none"
                data-testid="receipt-date-input"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Betrag (€)</label>
            <input
              type="number"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
              className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 outline-none font-mono text-xl"
              placeholder="0,00"
              data-testid="receipt-amount-input"
            />
          </div>

          {/* Kostenstelle Selection */}
          <div>
            <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-3">
              Kostenstelle (für Nebenkostenabrechnung)
            </label>
            <div className="space-y-3">
              {/* Umlagefähig */}
              <div>
                <p className="text-xs font-medium text-emerald-700 mb-2 flex items-center gap-1">
                  <Check weight="bold" className="w-3 h-3" /> Umlagefähig nach BetrKV
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {umlagefaehigeKs.map(ks => (
                    <button
                      key={ks.id}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, kostenstelle_id: ks.id }))}
                      className={`p-2 text-left text-sm border transition-colors ${
                        formData.kostenstelle_id === ks.id 
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-800' 
                          : 'border-gray-200 hover:border-emerald-300 hover:bg-emerald-50/50'
                      }`}
                    >
                      {ks.name}
                    </button>
                  ))}
                </div>
              </div>
              {/* Nicht umlagefähig */}
              <div>
                <p className="text-xs font-medium text-amber-700 mb-2 flex items-center gap-1">
                  <Warning weight="bold" className="w-3 h-3" /> Nicht umlagefähig
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {nichtUmlagefaehigeKs.map(ks => (
                    <button
                      key={ks.id}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, kostenstelle_id: ks.id }))}
                      className={`p-2 text-left text-sm border transition-colors ${
                        formData.kostenstelle_id === ks.id 
                          ? 'border-amber-500 bg-amber-50 text-amber-800' 
                          : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50/50'
                      }`}
                    >
                      {ks.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Property & Unit */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Immobilie</label>
              <select
                value={formData.property_id}
                onChange={(e) => setFormData(prev => ({ ...prev, property_id: e.target.value, unit_id: '' }))}
                className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 outline-none"
                data-testid="receipt-property-select"
              >
                <option value="">Keine Zuordnung</option>
                {properties.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs tracking-widest uppercase font-bold text-slate-500 mb-2">Einheit</label>
              <select
                value={formData.unit_id}
                onChange={(e) => setFormData(prev => ({ ...prev, unit_id: e.target.value }))}
                className="w-full border-b border-gray-300 bg-transparent py-2 focus:border-slate-900 outline-none"
                disabled={!formData.property_id}
                data-testid="receipt-unit-select"
              >
                <option value="">Gesamte Immobilie</option>
                {filteredUnits.map(u => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Status Info */}
          {formData.kostenstelle_id && (
            <div className={`p-4 ${formData.umlagefaehig ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}>
              <p className={`text-sm font-medium ${formData.umlagefaehig ? 'text-emerald-800' : 'text-amber-800'}`}>
                {formData.umlagefaehig 
                  ? '✓ Dieser Betrag wird in der Nebenkostenabrechnung auf die Mieter umgelegt.'
                  : '⚠ Dieser Betrag ist nicht umlagefähig und wird vom Vermieter getragen.'}
              </p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-gray-200 text-slate-900 py-2.5 font-medium hover:bg-gray-50"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-slate-900 text-white py-2.5 font-medium hover:bg-slate-800 disabled:opacity-50"
              data-testid="receipt-save-button"
            >
              {loading ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
